import type {
  EntityPreview,
  EntityType,
  MatchpropMode
} from "@dxf-ai-editor/contracts";

// Imported from `previewBuilders` (where every list is DEFINED), not from the
// `simpleGeometry` barrel that merely re-exports them: the barrel also
// `export *`s `extendedPropertyMutations`, so the barrel edge made this module
// (and everything above it — descriptors/common → typedPropertyDescriptors →
// typedPropertyWriter) a transitive dependent of the SHELL applier. That edge
// blocked the S15 Slice A shell↔document mirror (@codex P2 round on c8394b70):
// `extendedPropertyMutations` must import the typed-writer gate, which would
// have closed a depcruise cycle through the barrel. Direct-source import keeps
// the graph acyclic (ПРИОРИТЕТ #2 — zero-cycle gate).
import {
  simpleGeometryArcPreviewProperties,
  simpleGeometryCirclePreviewProperties,
  simpleGeometryLinePreviewProperties,
  simpleGeometryPointPreviewProperties,
  ellipsePreviewProperties,
  lwpolylinePreviewProperties,
  textPreviewProperties,
  mtextPreviewProperties,
  dimensionPreviewProperties,
  multileaderPreviewProperties,
  revisionCloudPreviewProperties,
  hatchPreviewProperties,
  blockInsertPreviewProperties,
  arrayRectPreviewProperties,
  viewportPreviewProperties,
  polylinePreviewProperties,
  tablePreviewProperties,
  oleFramePreviewProperties
} from "./previewBuilders";

export type PropertyChannel =
  | "properties"
  | "chprop"
  | "matchprop_basic"
  | "matchprop_special";

export type EntityPropertyCapabilities = {
  editableInProperties: string[];
  editableInChprop: string[];
  copyableInMatchpropBasic: string[];
  copyableInMatchpropSpecial: string[];
  requiresSpecialEditor: boolean;
  notes: string[];
};

export const commonObjectProperties = [
  "Color",
  "Layer",
  "Linetype",
  "Linetype Scale",
  "Lineweight",
  "Thickness",
  "Transparency",
  "Plot Style",
  "Material",
  "Annotative"
] as const;

const basicMatchpropCommon = [
  "Color",
  "Layer",
  "Linetype",
  "Linetype Scale",
  "Lineweight",
  "Thickness",
  "Transparency",
  "Plot Style"
] as const;

type CapabilitySeed = Omit<EntityPropertyCapabilities, "editableInChprop" | "copyableInMatchpropBasic"> & {
  commonOverrides?: Partial<Pick<EntityPropertyCapabilities, "editableInChprop" | "copyableInMatchpropBasic">>;
};

const capabilitySeeds: Record<EntityType, CapabilitySeed> = {
  Block: {
    editableInProperties: [...blockInsertPreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: true,
    notes: [
      "Block definition behavior stays outside generic MATCHPROP.",
      "Attributes and definition links need dedicated editors later."
    ]
  },
  Hatch: {
    editableInProperties: [...hatchPreviewProperties],
    copyableInMatchpropSpecial: [
      "Pattern",
      "Gradient",
      "Scale",
      "Angle",
      "Annotative",
      // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR — MATCHPROP's "Hatch" special
      // property copies the whole fill-pattern definition (name/type/scale/
      // angle/double), the same bundle Pattern/Scale/Angle above already
      // stand in for; Double is the remaining pattern-definition scalar. A
      // copy onto a target whose Pattern type != User defined silently
      // no-ops at the typed-writer boundary (resolveReadOnly), matching
      // AutoCAD (the copied flag is meaningless there).
      "Double"
    ],
    requiresSpecialEditor: true,
    notes: [
      "Hatch origin and boundary editing stay outside this first slice."
    ],
    commonOverrides: {
      editableInChprop: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style",
        "Material",
        "Annotative"
      ],
      copyableInMatchpropBasic: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style"
      ]
    }
  },
  "Rectangular Associative Array": {
    editableInProperties: [...arrayRectPreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: true,
    notes: [
      "Structural array parameters remain dedicated-editor territory.",
      "No special MATCHPROP category is claimed for arrays in this slice."
    ]
  },
  Line: {
    editableInProperties: [...simpleGeometryLinePreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: false,
    notes: [
      "This is part of the safest first tested slice for property parity."
    ]
  },
  Circle: {
    editableInProperties: [...simpleGeometryCirclePreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: false,
    notes: [
      "Basic object-property parity is safe here; geometry relations stay in dedicated commands."
    ]
  },
  Arc: {
    editableInProperties: [...simpleGeometryArcPreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: false,
    notes: [
      "Arc belongs to the safest tested slice together with line and circle."
    ]
  },
  Point: {
    // S15 Slice B — Position X/Y/Z + Rotation are typed-writer-persisted
    // (geometryReducer Step 1d), so the multi-select Properties palette
    // (`getSharedPropertiesForSelection(…, "properties")`) must advertise
    // them like every other writable kind. Grip drag is ALSO wired (S12b —
    // the single "location" grip commits through Position X/Y).
    editableInProperties: [...simpleGeometryPointPreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: false,
    notes: ["Point position/rotation edit through Properties (S15 Slice B) and grip drag (S12b)."]
  },
  Ellipse: {
    // S15 Slice B — center / major-axis vector / ratio / parameters are
    // typed-writer-persisted (geometryReducer Step 1d); same advertisement
    // rationale as Point above. Grip drag is ALSO wired
    // (GRIPS-DRAG-MORE-TYPES — center/major-axis/minor-axis grips).
    editableInProperties: [...ellipsePreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: false,
    notes: ["Ellipse geometry edit through Properties (S15 Slice B) and grip drag (GRIPS-DRAG-MORE-TYPES)."]
  },
  Spline: {
    editableInProperties: [],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: true,
    notes: [
      "SPLINE — full NURBS editing requires dedicated CV / fit-point editor.",
      "Generic MATCHPROP only copies basic object properties."
    ]
  },
  Wipeout: {
    editableInProperties: [],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: true,
    notes: ["Wipeout entity — frame display + clip boundary editing via dedicated commands."]
  },
  Polyline: {
    editableInProperties: [...polylinePreviewProperties],
    copyableInMatchpropSpecial: [
      // The same polyline special-MATCHPROP bundle AutoCAD copies for
      // LWPOLYLINE — a heavy polyline carries the identical width /
      // linetype-generation / closed semantics, so the copyable set matches.
      "Start segment width",
      "End segment width",
      "Linetype generation",
      "Closed",
    ],
    requiresSpecialEditor: false,
    notes: [
      "Legacy heavy POLYLINE (2D / 3D / polygon mesh / poly-face mesh).",
      "Mesh rows (M/N counts, smooth densities, surface type) read empty on a plain polyline, matching AutoCAD.",
    ],
  },
  Table: {
    editableInProperties: [...tablePreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: true,
    notes: [
      "ACAD_TABLE renders through the anonymous *T<N> block that carries its graphics.",
      "Cell values, formulas, cell styles, merges, TABLESTYLE management and table breaking are a separate feature and are NOT editable here.",
    ],
  },
  "OLE Frame": {
    editableInProperties: [...oleFramePreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: true,
    notes: [
      "OLE2FRAME draws as its frame rectangle — AutoCAD's own behaviour when the OLE server cannot render the object.",
      "The embedded OLE payload (an EMF inside a compound document) is retained verbatim and never decoded; rasterising it is a separate project.",
    ],
  },
  LWPOLYLINE: {
    editableInProperties: [...lwpolylinePreviewProperties],
    copyableInMatchpropSpecial: [
      "Constant width",
      "Linetype generation",
      "Vertices",
      "Closed"
    ],
    requiresSpecialEditor: false,
    notes: [
      "Polyline special MATCHPROP is documented and must stay explicit."
    ]
  },
  Text: {
    editableInProperties: [...textPreviewProperties],
    copyableInMatchpropSpecial: [
      "Text style",
      "Annotative"
    ],
    requiresSpecialEditor: false,
    notes: [
      "Alignment semantics should not be flattened to content-only editing."
    ]
  },
  MText: {
    editableInProperties: [...mtextPreviewProperties],
    copyableInMatchpropSpecial: [
      "Text style",
      "Annotative"
    ],
    requiresSpecialEditor: false,
    notes: [
      "Linetype and linetype scale are intentionally excluded from MText MATCHPROP basic."
    ],
    commonOverrides: {
      editableInChprop: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style",
        "Material",
        "Annotative"
      ],
      copyableInMatchpropBasic: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style"
      ]
    }
  },
  Dimension: {
    editableInProperties: [...dimensionPreviewProperties],
    copyableInMatchpropSpecial: [
      "Dimension style",
      "Annotative"
    ],
    requiresSpecialEditor: true,
    notes: [
      "Dimension geometry and associative semantics remain outside CHPROP."
    ],
    commonOverrides: {
      editableInChprop: [
        "Color",
        "Layer",
        "Linetype",
        "Linetype Scale",
        "Lineweight",
        "Transparency",
        "Plot Style",
        "Material",
        "Annotative"
      ],
      copyableInMatchpropBasic: [
        "Color",
        "Layer",
        "Linetype",
        "Linetype Scale",
        "Lineweight",
        "Transparency",
        "Plot Style"
      ]
    }
  },
  Multileader: {
    editableInProperties: [...multileaderPreviewProperties],
    copyableInMatchpropSpecial: [
      "Multileader style",
      "Annotative"
    ],
    requiresSpecialEditor: true,
    notes: [
      "Multileader content/block graph stays out of generic property copy."
    ],
    commonOverrides: {
      editableInChprop: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style",
        "Material",
        "Annotative"
      ],
      copyableInMatchpropBasic: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style"
      ]
    }
  },
  "Revision Cloud": {
    editableInProperties: [...revisionCloudPreviewProperties],
    copyableInMatchpropSpecial: [],
    requiresSpecialEditor: true,
    notes: [
      "Revcloud-specific MATCHPROP parity is intentionally not claimed in this slice."
    ],
    commonOverrides: {
      editableInChprop: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style",
        "Material",
        "Annotative"
      ],
      copyableInMatchpropBasic: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style"
      ]
    }
  },
  Viewport: {
    editableInProperties: [...viewportPreviewProperties],
    // "Shade plot" (DXF group 170) is DISPLAY-ONLY: shown in the PROPERTIES grid
    // but not copyable via MATCHPROP. It is edited via the dedicated
    // ViewportInspector dropdown (command path: applyViewportSetShadePlot, full
    // guards + faithful undo). MATCHPROP-copy persistence for the viewport-special
    // fields is a DEFERRED family-wide subsystem — none of the six (Shade plot +
    // Display lock/Scale/Snap/Grid/UCS icon) persist via copy yet.
    copyableInMatchpropSpecial: [
      "Display lock",
      "Scale",
      "Snap",
      "Grid",
      "UCS icon"
    ],
    requiresSpecialEditor: true,
    notes: [
      "Clipping and per-viewport freeze/thaw remain out of MATCHPROP special."
    ],
    commonOverrides: {
      editableInChprop: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style"
      ],
      copyableInMatchpropBasic: [
        "Color",
        "Layer",
        "Lineweight",
        "Transparency",
        "Plot Style"
      ]
    }
  }
};

export const propertyCapabilityMap: Record<EntityType, EntityPropertyCapabilities> = Object.fromEntries(
  Object.entries(capabilitySeeds).map(([entityType, seed]) => [
    entityType,
    {
      editableInProperties: seed.editableInProperties,
      editableInChprop: seed.commonOverrides?.editableInChprop ?? [...commonObjectProperties],
      copyableInMatchpropBasic:
        seed.commonOverrides?.copyableInMatchpropBasic ?? [...basicMatchpropCommon],
      copyableInMatchpropSpecial: seed.copyableInMatchpropSpecial,
      requiresSpecialEditor: seed.requiresSpecialEditor,
      notes: seed.notes
    }
  ])
) as Record<EntityType, EntityPropertyCapabilities>;

export function getEntityPropertyCapabilities(entityType: EntityType): EntityPropertyCapabilities {
  return propertyCapabilityMap[entityType];
}

export function getPropertiesForChannel(
  entityType: EntityType,
  channel: PropertyChannel
): string[] {
  const capabilities = getEntityPropertyCapabilities(entityType);

  switch (channel) {
    case "properties":
      return capabilities.editableInProperties;
    case "chprop":
      return capabilities.editableInChprop;
    case "matchprop_basic":
      return capabilities.copyableInMatchpropBasic;
    case "matchprop_special":
      return capabilities.copyableInMatchpropSpecial;
    default:
      return [];
  }
}

export function isCommonObjectProperty(propertyName: string): boolean {
  return commonObjectProperties.includes(propertyName as (typeof commonObjectProperties)[number]);
}

export function getSharedPropertiesForSelection(
  entities: EntityPreview[],
  channel: PropertyChannel
): string[] {
  const [firstEntity, ...rest] = entities;

  if (!firstEntity) {
    return [];
  }

  const firstProperties = getPropertiesForChannel(firstEntity.type, channel).filter((propertyName) =>
    firstEntity.editableProperties.includes(propertyName) || propertyName in firstEntity.properties
  );

  return firstProperties.filter((propertyName) =>
    rest.every((entity) => {
      const entityChannelProperties = getPropertiesForChannel(entity.type, channel);
      const entityCanSurfaceProperty =
        entity.editableProperties.includes(propertyName) || propertyName in entity.properties;

      return entityChannelProperties.includes(propertyName) && entityCanSurfaceProperty;
    })
  );
}

export function resolveMatchpropChannels(mode: MatchpropMode): PropertyChannel[] {
  switch (mode) {
    case "basic":
      return ["matchprop_basic"];
    case "special":
      return ["matchprop_special"];
    case "all":
      return ["matchprop_basic", "matchprop_special"];
    default:
      return ["matchprop_basic"];
  }
}
